// Release B · B3 frontend — Owner Access-Review Workspace (list view).
// Route: /admin/access-requests
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const STATES = ["", "submitted", "email_verified", "owner_review",
  "questionnaire_requested", "questionnaire_submitted",
  "additional_information_requested", "interview_scheduled",
  "approved", "rejected", "invitation_sent", "invitation_accepted",
  "activated", "withdrawn", "expired", "blocked", "needs_classification"];

const STATE_CHIP = {
  submitted:                          "#94a3b8",
  email_verified:                     "#0ea5e9",
  owner_review:                       "#f59e0b",
  needs_classification:               "#a855f7",
  questionnaire_requested:            "#f97316",
  questionnaire_submitted:            "#14b8a6",
  additional_information_requested:   "#eab308",
  interview_scheduled:                "#6366f1",
  approved:                           "#22c55e",
  rejected:                           "#dc2626",
  blocked:                            "#7f1d1d",
  invitation_sent:                    "#22c55e",
  invitation_accepted:                "#059669",
  activated:                          "#166534",
  withdrawn:                          "#64748b",
  expired:                            "#64748b",
};

function StateChip({ state }) {
  const c = STATE_CHIP[state] || "#64748b";
  return <span data-testid={`ar-state-${state}`} style={{
    display: "inline-block", padding: "2px 10px", borderRadius: 9999,
    background: c + "22", color: c, border: `1px solid ${c}55`,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
  }}>{state}</span>;
}

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function AccessRequestsInbox() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [rel, setRel] = useState("");
  const [needsClassification, setNeedsClassification] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (rel) params.set("requested_relationship", rel);
      if (needsClassification) params.set("needs_classification", needsClassification);
      if (q) params.set("q", q);
      params.set("limit", "50");
      const r = await api.get(`/admin/access-requests?${params.toString()}`);
      setRows(r.data.items || []); setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load access requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, rel, needsClassification]);

  return (
    <AdminAppShell current="/admin/access-requests">
      <div data-testid="access-requests-inbox" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Portal Access Requests</h1>
            <div style={{ color: "#475569", fontSize: 13, marginTop: 4 }}>
              Every submitted portal-access application. Persistence-first; Owner-only decisions.
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#475569" }}>Total: <b>{total}</b></div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "16px 0" }}>
          <select data-testid="ar-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            {STATES.map(s => <option key={s} value={s}>{s || "All states"}</option>)}
          </select>
          <select data-testid="ar-filter-needs-class" value={needsClassification} onChange={(e) => setNeedsClassification(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            <option value="">Any classification</option>
            <option value="true">Needs classification (Other)</option>
            <option value="false">Classified</option>
          </select>
          <Input data-testid="ar-filter-search" placeholder="Search name / email / phone / business…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} style={{ maxWidth: 320 }} />
          <Button data-testid="ar-filter-apply" onClick={load}>Search</Button>
        </div>

        <Card>
          <CardContent style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", background: "#f8fafc" }}>
                  <th style={hStyle}>State</th>
                  <th style={hStyle}>Applicant</th>
                  <th style={hStyle}>Relationship</th>
                  <th style={hStyle}>City / State</th>
                  <th style={hStyle}>Received</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} style={{ padding: 16, color: "#64748b" }}>Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={5} style={{ padding: 16, color: "#64748b" }}>No access requests match these filters.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`ar-row-${r.id}`} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={cellStyle}><StateChip state={r.status} />{r.needs_classification && <span data-testid="ar-needs-class-badge" style={{ marginLeft: 6, fontSize: 10, color: "#a855f7" }}>needs classification</span>}</td>
                    <td style={cellStyle}>
                      <div><b>{r.applicant_snapshot?.full_legal_name}</b></div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{r.applicant_snapshot?.email} · {r.applicant_snapshot?.phone || "—"}</div>
                      {r.applicant_snapshot?.business_name && <div style={{ fontSize: 11, color: "#64748b" }}>{r.applicant_snapshot.business_name}</div>}
                    </td>
                    <td style={cellStyle}>
                      {r.requested_relationship}
                      {r.other_relationship_description && <div style={{ fontSize: 11, color: "#64748b" }}>({r.other_relationship_description.slice(0, 60)})</div>}
                    </td>
                    <td style={cellStyle}>{r.applicant_snapshot?.city} / {r.applicant_snapshot?.state}</td>
                    <td style={{ ...cellStyle, fontSize: 12 }}>{fmt(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div style={{ marginTop: 12, fontSize: 11, color: "#64748b" }}>
          Actions (classify, request info, reject, block, approve, note) available via API today at <code>/api/admin/access-requests/{"{id}"}</code>. A detail page is not implemented.
        </div>
      </div>
    </AdminAppShell>
  );
}

const hStyle = { padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 };
const cellStyle = { padding: 10, fontSize: 13, verticalAlign: "top" };
